import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const giragliaSaintTropezEn: ChauffeurPage = {
  slug: "giraglia-saint-tropez",
  title: "Private chauffeur Giraglia Saint-Tropez – regatta VTC",
  subtitle:
    "Private chauffeur during the Giraglia in Saint-Tropez: access to the port and race village, airport transfers and shuttles around the gulf.",
  keywords: [
    "giraglia saint-tropez chauffeur",
    "giraglia regatta chauffeur",
    "saint-tropez regatta vtc",
  ],
  hero: {
    title: "Private chauffeur during the Giraglia in Saint-Tropez",
    subtitle:
      "A regatta linking Saint-Tropez to Genoa via the Giraglia rock, in June: a dedicated chauffeur when the peninsula saturates and the port fills.",
    keywords: [
      "Giraglia chauffeur",
      "Saint-Tropez regatta VTC",
      "gulf of Saint-Tropez shuttle",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Giraglia in Saint-Tropez",
    badges: ["Airport transfers", "Port & village access", "Gulf shuttles"],
  },
  benefits: {
    heading: "Why a chauffeur during the Giraglia",
    items: [
      {
        title: "A congested peninsula handled",
        description:
          "A single road leads to Saint-Tropez: in June, it saturates. The chauffeur anticipates the windows and gets you near the port without parking.",
      },
      {
        title: "Airport transfers",
        description:
          "From Nice Côte d'Azur, or the closer Toulon-Hyères: transfer with flight tracking to Saint-Tropez.",
      },
      {
        title: "Port and race village",
        description:
          "Drop-off as close as possible to the port and quays where crews and owners gather during the Giraglia.",
      },
      {
        title: "Gulf shuttles",
        description:
          "Sainte-Maxime, Grimaud, Ramatuelle: links around the gulf of Saint-Tropez for dinners and accommodation.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Giraglia",
    services: [
      {
        title: "Airport ↔ Saint-Tropez transfer",
        text: "From Nice Côte d'Azur or Toulon-Hyères, with flight tracking during the Giraglia.",
      },
      {
        title: "Port & race-village access",
        text: "Drop-off as close as possible to the quays, depending on crowds and closures around the port.",
      },
      {
        title: "Gulf shuttles",
        text: "Saint-Tropez, Sainte-Maxime, Grimaud and Ramatuelle links for crews and guests.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur to combine inshore races, the village and meetings.",
      },
      {
        title: "Evenings & dinners",
        text: "Port restaurants and Pampelonne beaches, with a guaranteed late-night return.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Giraglia",
    items: [
      {
        name: "Old port of Saint-Tropez",
        description: "The regatta quays, where crews and owners gather.",
      },
      {
        name: "Village & centre of Saint-Tropez",
        description: "Place des Lices, lanes and central addresses.",
      },
      {
        name: "Pampelonne beaches (Ramatuelle)",
        description: "Seaside dinners and evenings.",
      },
      {
        name: "Sainte-Maxime & Grimaud",
        description: "Accommodation and links from the other side of the gulf.",
      },
      {
        name: "Airports (Nice, Toulon-Hyères)",
        description: "Gateways to the Saint-Tropez peninsula.",
      },
    ],
  },
  generic: {
    heading: "The Giraglia from a chauffeur's seat",
    subheading:
      "In June, Saint-Tropez launches the Giraglia regatta and the peninsula fills with crews and guests.",
    paragraphs: [
      "Every June, the Giraglia links Saint-Tropez to Genoa by rounding the Giraglia rock off Cap Corse, after several days of inshore races in the gulf. The port and village concentrate crews, owners and partners: a chauffeur who knows the peninsula gets you near the quays without hunting for a space.",
      "Saint-Tropez is served by a single road, quickly saturated in June. From Nice Côte d'Azur airport, or the closer Toulon-Hyères, the chauffeur handles the transfer with flight tracking and absorbs the traffic.",
      "Between inshore races, the race village, port dinners and Pampelonne evenings, hourly hire keeps the same chauffeur through the day and secures the gulf links and late-night returns.",
    ],
    bullets: [
      "Access nearest the port",
      "Airport transfers with flight tracking",
      "Shuttles around the gulf of Saint-Tropez",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Giraglia",
    steps: [
      "Tell us your arrival airport, your accommodation in the gulf and your times.",
      "We plan the transfers and shuttles around the peninsula and port crowds.",
      "The chauffeur arrives in uniform, with flight tracking for your arrivals.",
      "Direct transfer or hourly hire for inshore races, the village and evenings.",
    ],
  },
  faq: [
    {
      q: "How do I book a chauffeur for the Giraglia in Saint-Tropez?",
      a: "Book in advance: in June, Saint-Tropez and its single road saturate during the regatta. Share your airport, your accommodation and your times for a dedicated chauffeur throughout.",
    },
    {
      q: "Which airport do you transfer from?",
      a: "From Nice Côte d'Azur, the main gateway, or Toulon-Hyères, closer to the peninsula. In both cases, with flight tracking and adjustment to delays.",
    },
    {
      q: "Can you drop me as close as possible to the port and race village?",
      a: "Yes. The chauffeur drops you at the nearest authorised point to the quays, depending on crowds and closures, then collects you on the way out.",
    },
    {
      q: "Do you offer shuttles around the gulf of Saint-Tropez?",
      a: "Yes. We run links between Saint-Tropez, Sainte-Maxime, Grimaud and Ramatuelle for crews, guests and accommodation.",
    },
    {
      q: "Do you cover returns after dinners and evenings?",
      a: "Yes. Port dinners and Pampelonne evenings end late: the chauffeur waits and guarantees the late-night return to your accommodation.",
    },
    {
      q: "Why choose KS Rent Services for the Giraglia?",
      a: "Our private chauffeur service on the French Riviera is rated 5.0/5 from 38 Google reviews: punctuality and knowledge of Saint-Tropez and its gulf.",
    },
  ],
  relatedLinks: [
    {
      label: "Chauffeur service Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    {
      label: "Chauffeur hire on the Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    { label: "Nice Airport rates and routes", href: "/en/chauffeur/airport" },
    {
      label: "Car hire in Saint-Tropez",
      href: "/en/location/car-rental-saint-tropez",
    },
  ],
  cta: {
    title: "Your chauffeur for the Giraglia",
    text: "Airport transfers, port access and gulf shuttles: book a dedicated chauffeur for the regatta.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Giraglia Saint-Tropez Chauffeur — regatta car service",
    description:
      "Private chauffeur during the Giraglia in Saint-Tropez: airport transfers with flight tracking, access to the port and race village, gulf shuttles. Book now.",
    canonical: "/en/chauffeur/giraglia-saint-tropez",
  },
};

export default giragliaSaintTropezEn;
